import React, { useState } from 'react';
import { Edit, TabbedForm, TextInput, useCreatePath, useNotify, useRedirect } from 'react-admin';
import { Box, Container, Typography } from '@mui/material';
import { ReactComponent as DeleteIcon } from 'src/assets/trash-can.svg';
import { editContainerStyles, shsButtonStyles } from 'src/styles';
import ShsFormToolbar from 'src/components/common/ShsFormToolbar';
import {
    ShsButton,
    ShsReturnButton,
    WithRole,
    AutoSaveFormListener,
    ShsSuccessNotification
} from 'src/components/common';
import { useFormAutoSave } from 'src/hooks/useFormAutoSave';
import { AUTOSAVE_RESOURCES } from 'src/constants/autoSave';
import { CenterDepartment, CenterLessonsPlace, GeneralCenterInfo } from 'src/components/EducationalCenters/FieldGroups';
import { centerDefaultValues, CenterFormFields } from 'src/constants/centers';
import { DeleteEducationCenterModal } from './modals/DeleteEducationCenterModal';
import { UserRole } from '../../auth/types';
import { HospitalWorkingInfo } from './FieldGroups/HospitalWorkingInfo';
import { NOTIFICATION_OPTIONS_SUCCESS } from 'src/constants/common';

export const EducationalCenterEdit: React.FC = () => {
    const redirect = useRedirect();
    const createPath = useCreatePath();
    const notify = useNotify();
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

    const { storageKey, clearAutoSave, markAsSubmitting } = useFormAutoSave(AUTOSAVE_RESOURCES.CENTER);
    const redirectToCentersList = () => redirect(createPath({ resource: 'centers', type: 'list' }));

    const onCancelEdit = () => {
        clearAutoSave();
        redirectToCentersList();
    };

    const onSuccess = () => {
        markAsSubmitting();
        clearAutoSave();
        notify(<ShsSuccessNotification message="Центр успішно оновлено" />, NOTIFICATION_OPTIONS_SUCCESS);
        redirectToCentersList();
    };

    const onError = (error: any) => {
        notify(`Помилка: ${error?.message}`, {
            type: 'error'
        });
    };

    return (
        <>
            <Container sx={{ ...editContainerStyles, maxWidth: '776px' }} maxWidth={false}>
                <ShsReturnButton onClick={onCancelEdit} label="Назад до освітніх центрів" />
                <Edit
                    mutationMode="pessimistic"
                    queryOptions={{ meta: { useMock: false } }}
                    mutationOptions={{ onSuccess, onError }}>
                    <Typography variant="h4" component="h3" mb={4} fontWeight="700">
                        Редагування центру
                    </Typography>
                    <TabbedForm
                        toolbar={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <ShsFormToolbar
                                    sx={shsButtonStyles}
                                    enableSaveButton={true}
                                    handleCancel={onCancelEdit}
                                    shouldCheckIfDirty={false}
                                />

                                <WithRole roles={[UserRole.SYSTEM_ADMIN]}>
                                    <ShsButton
                                        label="Видалити центр"
                                        sx={{
                                            marginTop: '2rem',
                                            whiteSpace: 'nowrap',
                                            padding: '0.75em 1.75em'
                                        }}
                                        styleType="outline-danger"
                                        icon={<DeleteIcon style={{ fill: '#F23030' }} />}
                                        onClick={() => setDeleteModalOpen(true)}
                                    />
                                </WithRole>
                            </Box>
                        }
                        defaultValues={centerDefaultValues}>
                        <TabbedForm.Tab label="Загальна інформація">
                            {storageKey && <AutoSaveFormListener storageKey={storageKey} />}
                            <TextInput sx={{ display: 'none' }} source={CenterFormFields.VERSION} name="version" />
                            <GeneralCenterInfo />
                        </TabbedForm.Tab>
                        <TabbedForm.Tab label="Лікарня">
                            <HospitalWorkingInfo />
                        </TabbedForm.Tab>
                        <TabbedForm.Tab label="Відділення">
                            <CenterDepartment isEditMode />
                        </TabbedForm.Tab>
                        <TabbedForm.Tab label="Місця проведення уроків">
                            <CenterLessonsPlace isEditMode />
                        </TabbedForm.Tab>
                    </TabbedForm>
                    <DeleteEducationCenterModal isOpen={isDeleteModalOpen} onClose={() => setDeleteModalOpen(false)} />
                </Edit>
            </Container>
        </>
    );
};
